// s7 Журнал визитов (Mini-sprint 6-visits Stage 4 §4).
// Список визитов user'а, сгруппированный по месяцам, server-side поиск (debounce 300ms).

import { useState } from 'react';
import { useLocation } from 'wouter';
import { Calendar, Plus, Search, Paperclip } from 'lucide-react';

import { MobileFrame } from '@/components/layout/MobileFrame';
import { BottomNav } from '@/components/layout/BottomNav';

import { useVisitsInfinite } from '@/api/hooks/visits';
import { useDebounce } from '@/hooks/useDebounce';
import { formatVisitDate, groupVisitsByMonth, pluralVisits } from '@/lib/visit-format';
import type { Visit } from '@/api/types';

export function VisitsTab() {
  const [, setLocation] = useLocation();
  const [searchInput, setSearchInput] = useState('');
  const q = useDebounce(searchInput, 300);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useVisitsInfinite(q || undefined);

  const visits = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.meta.total ?? 0;
  const groups = groupVisitsByMonth(visits);

  return (
    <MobileFrame>
      {/* Inline header (ScreenHeader не принимает subtitle/right-action) */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-3 pb-3 pt-2">
        <div className="flex-1">
          <div className="text-base font-semibold leading-tight">Визиты к врачу</div>
          {total > 0 && (
            <div className="text-hl-small text-muted-foreground">
              {total} {pluralVisits(total)}
            </div>
          )}
        </div>
        {/* HEAL-62: «+» уехал из шапки в плавающую кнопку внизу справа. */}
      </div>

      {/* HEAL-62: обёртка нужна, чтобы плавающая кнопка позиционировалась
          относительно области списка, а не скроллилась вместе с ним. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <main className="flex flex-1 flex-col overflow-y-auto px-5 pb-24 pt-4">
          {/* Search */}
          <div className="relative mb-4 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Поиск по визитам, врачу, диагнозу"
              className="h-9 w-full rounded-control border border-border bg-muted pl-9 pr-3 text-hl-secondary"
            />
          </div>

          {isLoading && <ListSkeleton />}
          {error && <ListError onRetry={() => window.location.reload()} />}
          {!isLoading && !error && total === 0 && !q && (
            <EmptyState onAdd={() => setLocation('/visits/new')} />
          )}
          {!isLoading && !error && total === 0 && q && <NoSearchResults q={q} />}
          {!isLoading && !error && groups.length > 0 && (
            <div className="space-y-5">
              {groups.map((g) => (
                <div key={g.monthLabel}>
                  <div className="mb-2 text-hl-small font-bold uppercase tracking-wider text-muted-foreground">
                    {g.monthLabel}
                  </div>
                  <div className="space-y-2">
                    {g.visits.map((v) => (
                      <VisitCard key={v.id} visit={v} onTap={() => setLocation(`/visits/${v.id}`)} />
                    ))}
                  </div>
                </div>
              ))}
              {hasNextPage && (
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full rounded-surface border border-border bg-surface py-3 text-sm font-semibold text-primary-text hover:border-primary disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
                </button>
              )}
              {/* HEAL-62: пунктирная «Добавить визит» под списком убрана — рядом с
                  плавающей кнопкой это третий одинаковый вход в ту же форму. */}
            </div>
          )}
        </main>

        {/* HEAL-62: плавающая кнопка. Не `fixed`, а absolute внутри области списка,
            которая заканчивается там, где начинается BottomNav → кнопка физически
            не может его перекрыть, и свой safe-area ей не нужен (нижний инсет уже
            держит MobileFrame). Списку добавлен pb-24, чтобы последняя карточка
            доскроллилась из-под кнопки. */}
        <button
          type="button"
          onClick={() => setLocation('/visits/new')}
          className="absolute bottom-4 right-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition active:scale-95"
          aria-label="Добавить визит"
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      <BottomNav active="visits" />
    </MobileFrame>
  );
}

function VisitCard({ visit, onTap }: { visit: Visit; onTap: () => void }) {
  const hasAttachment = visit.attachments.length > 0;
  const meta = [visit.doctor_name, visit.clinic_name].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={onTap}
      className="block w-full rounded-surface border border-border bg-surface p-3 text-left hover:border-primary"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-sm font-semibold leading-tight">{visit.diagnosis}</div>
          <div className="mt-1 flex items-center gap-1.5 text-hl-small text-muted-foreground">
            <span className="truncate">
              {meta || <em className="text-gray-400">Без врача и клиники</em>}
            </span>
            {hasAttachment && <Paperclip className="h-3 w-3 flex-shrink-0 text-gray-400" />}
          </div>
        </div>
        <div className="flex-shrink-0 text-hl-small text-muted-foreground">
          {formatVisitDate(visit.visit_date)}
        </div>
      </div>
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary-text">
        <Calendar className="h-8 w-8" />
      </div>
      <div className="mb-1 text-base font-bold">Здесь будут ваши визиты к врачу</div>
      <div className="mb-6 text-sm text-muted-foreground">Сохраняйте заключения, чтобы не терять историю.</div>
      <button
        onClick={onAdd}
        className="h-10 rounded-control bg-primary px-5 text-sm font-semibold text-primary-foreground"
      >
        Добавить первый визит
      </button>
    </div>
  );
}

function NoSearchResults({ q }: { q: string }) {
  return (
    <div className="py-8 text-center text-sm text-muted-foreground">
      Ничего не найдено по запросу «{q}»
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
      {[0, 1].map((i) => (
        <div key={i} className="h-16 animate-pulse rounded-surface bg-muted" />
      ))}
    </div>
  );
}

function ListError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="py-8 text-center">
      <p className="mb-3 text-sm text-muted-foreground">Не удалось загрузить визиты.</p>
      <button onClick={onRetry} className="text-sm font-semibold text-primary-text">
        Повторить
      </button>
    </div>
  );
}
